import { HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Action, Actions, Selector, State, StateContext, Store } from '@ngxs/store';
import {
    AddUserSectionStats,
    AddUserSectionStatsForParticipant,
    FetchUserSectionStats,
    FetchUserSectionStatsFailure,
    FetchUserSectionStatsForParticipant,
    FetchUserSectionStatsForParticipantFailure,
    FetchUserSectionStatsForParticipantSuccess,
    FetchUserSectionStatsSuccess,
    SectionCompletionForParticipant,
    SectionCompletionForParticipantFailure,
    SectionCompletionForParticipantSuccess,
    SortUserSectionStats,
    UnsetUserSectionStatsForParticipant,
} from 'app/library/user-section/actions';
import { UserSectionStats } from 'app/library/user-section/models/user-section.model';
import { UserSectionStatsState } from 'app/library/user-section/models/user-section.state';
import { SubentityState } from 'app/projects/subentity/src/lib/models/subentity.state';
import { UtilsState } from 'app/projects/subentity/src/lib/models/utils.state';
import { map } from 'rxjs/operators';
import { SearchUserSectionStats } from '../actions';
import { SetUserSectionStats } from '../actions/index';
import { UserSectionApiService } from '../services/user-section.api-service';
import { UserSectionsStatsStateModel } from './user-sections.state-model';

@State<UserSectionsStatsStateModel>({
    name: 'userSectionsStats',
    defaults: new UserSectionsStatsStateModel(),
})
@Injectable()
export class UserSectionsStatsState extends SubentityState<UserSectionStats> {
    @Selector([UserSectionStatsState.getEntities])
    static getEntities(state: UserSectionsStatsStateModel, modules: UserSectionStats[]): UserSectionStats[] {
        if (!modules) {
            return null;
        }

        return modules
            .filter((item) => state.list.indexOf(item.id) !== -1)
            .map((item) => {
                return [state.list.indexOf(item.id), item];
            })
            .sort((a: [number, UserSectionStats], b: [number, UserSectionStats]) => {
                return a[0] > b[0] ? 1 : b[0] > a[0] ? -1 : 0;
            })
            .map((x: [number, UserSectionStats]) => x[1]);
    }

    @Selector([UserSectionStatsState.getEntities])
    static getFilteredEntities(state: UserSectionsStatsStateModel, stats: UserSectionStats[]): UserSectionStats[] {
        if (!stats) {
            return null;
        }

        return stats
            .filter((item) => state.filtered.indexOf(item.id) !== -1)
            .map((item) => {
                return [state.list.indexOf(item.id), item];
            })
            .sort((a: [number, UserSectionStats], b: [number, UserSectionStats]) => {
                return a[0] > b[0] ? 1 : b[0] > a[0] ? -1 : 0;
            })
            .map((x: [number, UserSectionStats]) => x[1]);
    }

    @Selector()
    static getSelectedEntityIds(state: UserSectionsStatsStateModel): string[] {
        return state.selected;
    }

    @Selector()
    static getSearchTerm(state: UserSectionsStatsStateModel): string {
        return state.searchTerm;
    }

    @Selector()
    static getTotalCount(state: UserSectionsStatsStateModel): number {
        return state.totalCount;
    }

    @Selector()
    static getPageSize(state: UserSectionsStatsStateModel): number {
        return state.pageSize;
    }

    @Selector()
    static getPageNumber(state: UserSectionsStatsStateModel): number {
        return state.pageNumber || 1;
    }

    @Selector()
    static getPageIndex(state: UserSectionsStatsStateModel): number {
        return state.pageNumber ? state.pageNumber - 1 : 0;
    }

    @Selector()
    static getPercentage(state: UserSectionsStatsStateModel): number {
        return state.statsPercentage;
    }

    constructor(private store: Store, private action$: Actions, private userSectionService: UserSectionApiService) {
        super(null, 0);

        this._propstoSearch = [...UserSectionStats.props_to_search];
    }

    @Selector()
    static isFetchingList(state: UserSectionsStatsStateModel): boolean {
        return state.fetchingList;
    }

    @Action(SetUserSectionStats)
    setAll(ctx: StateContext<UserSectionsStatsStateModel>, action: SetUserSectionStats): void {
        this._setSubentities(ctx, action, AddUserSectionStats);
    }

    @Action(FetchUserSectionStats)
    fetchEntities(ctx: StateContext<UserSectionsStatsStateModel>, action: FetchUserSectionStats): void {
        this._fetchSubentities(
            ctx,
            { payload: 'all' },
            SetUserSectionStats,
            FetchUserSectionStatsSuccess,
            FetchUserSectionStatsFailure,
            this.userSectionService
                .getModuleStats(
                    action.payload.moduleId,
                    action.payload.pageSize,
                    action.payload.pageNumber,
                    action.payload.sortBy,
                    action.payload.sortOrder,
                    action.payload.search,
                    action.payload.status,
                    action.payload.sectionId
                )
                .pipe(
                    map((userSectionStatsAndPercentage) => {
                        ctx.patchState({
                            statsPercentage: userSectionStatsAndPercentage.statsPercentage,
                            totalCount: userSectionStatsAndPercentage.pagination.number_of_results,
                        });

                        userSectionStatsAndPercentage.statsParticipants.forEach((participant) =>
                            participant.sectionStats.sort((a, b) => (a.section.sort > b.section.sort ? 1 : -1))
                        );

                        return userSectionStatsAndPercentage.statsParticipants;
                    })
                )
        );
    }

    @Action(FetchUserSectionStatsSuccess)
    fetchEntitiesSuccess(ctx: StateContext<UserSectionsStatsStateModel>): void {
        this._fetchSubentitiesSuccess(ctx);
    }

    @Action(FetchUserSectionStatsFailure)
    fetchEntitiesFailure(ctx: StateContext<UserSectionsStatsStateModel>): void {
        this._fetchSubentitiesFailure(ctx);
    }

    @Action(FetchUserSectionStatsForParticipant)
    fetchEntitiesForParticipant(ctx: StateContext<UserSectionsStatsStateModel>, action: FetchUserSectionStatsForParticipant): void {
        this._fetchSubentities(
            ctx,
            { payload: 'all' },
            SetUserSectionStats,
            FetchUserSectionStatsForParticipantSuccess,
            FetchUserSectionStatsForParticipantFailure,
            this.userSectionService.getModuleStatsForParticipant(action.payload.moduleId, action.payload.participantId).pipe(
                map(
                    (userSectionStatsAndPercentage) => {
                        ctx.dispatch(new FetchUserSectionStatsForParticipantSuccess());
                        ctx.patchState({
                            statsPercentage: userSectionStatsAndPercentage.statsPercentage,
                            totalCount: userSectionStatsAndPercentage.pagination.number_of_results,
                        });

                        const currentUserSectionStats = userSectionStatsAndPercentage.statsParticipants.map((userSectionStats) => userSectionStats.sectionStats)[0];
                        currentUserSectionStats.sort((a, b) => (a.section.sort > b.section.sort ? 1 : -1));

                        userSectionStatsAndPercentage.statsParticipants[0].sectionStats = currentUserSectionStats;
                        return userSectionStatsAndPercentage.statsParticipants;
                    },
                    (error: HttpErrorResponse) => {
                        ctx.dispatch(new FetchUserSectionStatsForParticipantFailure());
                    }
                )
            )
        );
    }

    @Action(FetchUserSectionStatsForParticipantSuccess)
    fetchUserSectionStatsForParticipantSuccess(ctx: StateContext<UserSectionsStatsStateModel>): void {
        this._fetchSubentitiesSuccess(ctx);
    }

    @Action(FetchUserSectionStatsForParticipantFailure)
    fetchUserSectionStatsForParticipantFailure(ctx: StateContext<UserSectionsStatsStateModel>): void {
        this._fetchSubentitiesFailure(ctx);
    }

    @Action(SectionCompletionForParticipant)
    bulkSectionCompletion(ctx: StateContext<UserSectionsStatsStateModel>, action: SectionCompletionForParticipant): void {
        this.userSectionService
            .bulkSectionCompletion(action.payload.sections, action.payload.userId)
            .pipe(
                map((userSectionStatsAndPercentage) => {
                    ctx.patchState({
                        statsPercentage: userSectionStatsAndPercentage.statsPercentage,
                        totalCount: userSectionStatsAndPercentage.pagination.number_of_results,
                    });
                    const currentUserSectionStats = userSectionStatsAndPercentage.statsParticipants.map((userSectionStats) => userSectionStats.sectionStats)[0];
                    currentUserSectionStats.sort((a, b) => (a.section.sort > b.section.sort ? 1 : -1));

                    userSectionStatsAndPercentage.statsParticipants[0].sectionStats = currentUserSectionStats;
                    return userSectionStatsAndPercentage.statsParticipants;
                })
            )
            .subscribe(
                (entities) => {
                    ctx.dispatch([new AddUserSectionStatsForParticipant(entities), new SectionCompletionForParticipantSuccess()]);
                },
                (error: HttpErrorResponse) => {
                    ctx.dispatch(new SectionCompletionForParticipantFailure());
                }
            );
    }

    @Action(AddUserSectionStats)
    addEntities(ctx: StateContext<UserSectionsStatsStateModel>, action: AddUserSectionStats): void {
        const statsFromEntityStore = this.store.selectSnapshot(UserSectionStatsState.getEntities) || [];
        const stats = [...statsFromEntityStore, ...action.payload];

        this._patchSubentities(ctx, { payload: { subentityIds: action.payload.map(({ id }) => id) } }, stats);
    }

    @Action(AddUserSectionStatsForParticipant)
    addEntities2(ctx: StateContext<UserSectionsStatsStateModel>, action: AddUserSectionStatsForParticipant): void {
        const statsFromEntityStore = this.store.selectSnapshot(UserSectionStatsState.getEntities) || [];
        const stats = [...statsFromEntityStore, ...action.payload];

        this._patchSubentities(ctx, { payload: { subentityIds: action.payload.map(({ id }) => id) } }, stats);
    }

    @Action(UnsetUserSectionStatsForParticipant)
    unsetAll(ctx: StateContext<UserSectionsStatsStateModel>): void {
        this._unsetSubentities(ctx);
    }

    @Action(SearchUserSectionStats)
    search(ctx: StateContext<UserSectionsStatsStateModel>, action: SearchUserSectionStats): void {
        const stats = this.store.selectSnapshot(UserSectionStatsState.getEntities);

        this._searchSubentities(ctx, action, stats);
    }

    @Action(SortUserSectionStats)
    sort(stateContext: StateContext<UserSectionsStatsStateModel>, { payload }: SortUserSectionStats): void {
        UtilsState.sort(stateContext, payload, UserSectionsStatsStateModel.identifier);
    }
}
